import React, { useEffect, useState, memo } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { ReactComponent as IconAttachment } from "assets/images/icons/attachment.svg";
import { ReactComponent as IconApprove } from "assets/images/icons/approve.svg";
import { ReactComponent as IconReject } from "assets/images/icons/reject-request.svg";
import { ReactComponent as IconApproved } from "assets/images/icons/project-icons/approve-circle.svg";
import { ReactComponent as IconRejected } from "assets/images/icons/project-icons/rejected-circle.svg";
import { ReactComponent as IconPending } from "assets/images/icons/project-icons/pending.svg";
import FormDrawer from 'components/__new/common/form-drawer';
import Table from 'components/__new/common/Table';
import { TableContent } from 'components/__new/common/TableContent';
import CurrencyFormat from 'utils/currencyFormat';
import { getRandomColor } from 'utils/randomColor';
import DateFormat, { TimeFormat } from 'utils/dateFormat';
import { renderStatusColor } from 'utils/statusColor';
import Maybe from 'components/__new/common/Maybe';
import { decryptPayload } from 'utils/encrypt_decrypt';
import * as approvalAction from "store/entities/approvals/action";
import ContentLoading from 'components/__new/common/ContentLoading';
import { ButtonLoader } from 'components/__new/common/form';
import * as transfersAction from 'store/entities/transfers/action';
import { WorkflowType } from 'utils/workflowType';
import useLoginInfo from 'hooks/useLoginInfo';
import { nameInitials } from 'utils/nameInitials';

const RequestDetailsWrapper = ({title, isOpen, setIsOpen, request, getRequests, requestComments, onOpenApproveRequest, onOpenRejectRequest, onOpenCancelRequest}) => {
    const { userInfo } = useLoginInfo();
    const { requestComment, requestCommentLoading } = useSelector((s) => s.entities.approvals);
    const { earnipayTransferResponse, bankTransferResponse } = useSelector(s => s.entities.transfers);
    const dispatch = useDispatch();
    const [decryptedValue, setDecryptedValue] = useState([]);
    const [form, setForm] = useState({
        comment: "",
    });
    const [narration, setNarration] = useState("");
    const [destination, setDestination] = useState("");
    const [isLoading, setIsLoading] = useState("");

    const [commentColors, setCommentColors] = useState({});

    const approvalEmails = request?.approvalWorkflow?.map(el => el?.businessMember?.email);

    const postRequestComment = (e) => {
        e.preventDefault();
        const payload = {
            comment: form?.comment,
        }
        const requestId = request?.id
        dispatch(approvalAction.postRequestComment(payload, requestId))
    }

    const clearForm = () => {
        form.comment = "";
    }

    const isFormValid = !form?.comment.length > 0;

    const handleMakeTransfer = () => {
        setIsLoading(true);
        // e2e single transfer
        if (request?.type === WorkflowType?.TRANSFER_REQUEST && destination === "earnipay") {
            dispatch(transfersAction.initiateSingleEarnipayTransfer(decryptedValue[0]));
            dispatch(approvalAction.updateRequestDisbursement(request?.id));
        }

        // e2e multiple transfer
        if (request?.type === WorkflowType?.MULTIPLE_TRANSFER_REQUEST && destination === "earnipay") {
            dispatch(transfersAction.initiateMultipleEarnipayTransfer(decryptedValue[0]));
            dispatch(approvalAction.updateRequestDisbursement(request?.id));
        }

        // e2e bulk transfer
        // if (request?.type === WorkflowType?.BULK_TRANSFER && destination === "earnipay") {}

        // e2b single transfer
        if (request?.type === WorkflowType?.TRANSFER_REQUEST && destination === "bank") {
            dispatch(transfersAction.initiateSingleBankTransfer(decryptedValue[0]));
            dispatch(approvalAction.updateRequestDisbursement(request?.id));
        }

        // e2b multiple transfer
        if (request?.type === WorkflowType?.MULTIPLE_TRANSFER_REQUEST && destination === "bank") {
            dispatch(transfersAction.initiateMultipleBankTransfer(decryptedValue[0]));
            dispatch(approvalAction.updateRequestDisbursement(request?.id));
        }

        // e2b bulk transfer
        // if (request?.type === WorkflowType?.BULK_TRANSFER && destination === "bank") {}

        // payroll
        // if (request?.type === WorkflowType?.PAYROLL_REQUEST) {}

        // on demand
        // if (request?.type === WorkflowType?.ON_DEMAND_REQUEST) {}

        // single bill payment
        if (request?.type === WorkflowType?.BILL_PAYMENT_REQUEST) {}

        // multiple bill payment
        // if (request?.type === WorkflowType?.MULTIPLE_BILL_PAYMENT_REQUEST) {}

        // console.log("request:", request);
        // console.log("type:", request?.type);
        // console.log("decryptedValue:", decryptedValue);
        // console.log("destination:", destination);
    }

    useEffect(() => {
        if (requestComments?.data?.length > 0) {
            const colors = {};
            requestComments.data.forEach(comment => {
                const initials = nameInitials(comment?.businessMember?.firstName, comment?.businessMember?.lastName);
                if (!colors[initials]) {
                    colors[initials] = getRandomColor();
                }
            });
            setCommentColors(colors);
        }
    }, [requestComments]);

    useEffect(() => {
        if(request?.metaData) {
            const decryptedMetaData = decryptPayload(request?.metaData);
            setDestination(decryptedMetaData?.destination)
            setNarration(decryptedMetaData?.payload?.description || decryptedMetaData?.payload?.narration);
            if (Array.isArray(decryptedMetaData?.payload)) {
                setDecryptedValue(decryptedMetaData?.payload);
            } else {
                setDecryptedValue([decryptedMetaData?.payload]);
            }
        }
    }, [request]);

    useEffect(() => {
        if (requestComment?.error === false) {
            dispatch(approvalAction.getRequestComments(request?.id));
            clearForm();
        }
    }, [requestComment, dispatch]);

    useEffect(() => {
        // Success response for e2e single transfer
        if (earnipayTransferResponse) {
            getRequests();
            setIsLoading(false);
            setIsOpen(false);
            toast.success("Transfer successful");
            dispatch(transfersAction.resetEarnipayTransfer());
        }
        // Success response for e2b single transfer
        if (bankTransferResponse?.error === false) {
            getRequests();
            setIsLoading(false);
            setIsOpen(false);
            toast.success("Transfer successful");
            dispatch(transfersAction.resetBankTransfer());
        }
    }, [
            dispatch,
            earnipayTransferResponse,
            bankTransferResponse
        ]
    );

    return (
        <FormDrawer backdropClickable={false} display="center" size="4xl" isOpen={isOpen} setIsOpen={setIsOpen}>
            <div className='w-full p-7'>
                <FormDrawer.Title
                    title={title}
                    showCloseButton={true}
                    close={setIsOpen}
                />

                <div>
                    <div className="w-full mt-7 flex scrollable-notification-panel">
                        <div className='w-2/4 border-r px-2 overflow-y-auto'>
                            <div className='text-[#718096]'>Narration</div>
                            <div className='mt-2 font-medium'>{narration ?? "-"}</div>
                            <div className='text-[#718096] mt-7'>Recipients</div>
                            <Table>
                                <Table.Head>
                                    <th>To</th>
                                    <th>Amount</th>
                                </Table.Head>
                                <Table.Body>

                                    <TableContent
                                        loadingText="Loading recipients"
                                        data={decryptedValue}
                                        colspan={2}
                                    />

                                    {decryptedValue[0]?.recipients ? decryptedValue[0]?.recipients?.map((value, index) =>
                                        <tr key={index}>
                                            <td>
                                                <div className="flex items-center space-x-2">
                                                    {/* <div className="rounded-full w-12 h-12 flex justify-center items-center">
                                                        <IconAccess />
                                                    </div> */}
                                                    <div>
                                                        <div className="text-[#101828] font-medium my-1">{value?.accountName || "-"}</div>
                                                        <div className="my-1">{value?.bankName} • {value?.accountNumber}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div>
                                                    {CurrencyFormat(value?.amount || 0, true)}
                                                </div>
                                            </td>
                                        </tr>
                                    ) : decryptedValue?.map((value, index) =>
                                        <tr key={index}>
                                            <td>
                                                <div className="flex items-center space-x-2">
                                                    {/* <div className="rounded-full w-12 h-12 flex justify-center items-center">
                                                        <IconAccess />
                                                    </div> */}
                                                    <div>
                                                        <div className="text-[#101828] font-medium my-1">{value?.accountName || "-"}</div>
                                                        <div className="my-1">{value?.bankName} • {value?.accountNumber}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div>
                                                    {CurrencyFormat(value?.amount || 0, true)}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td>Total Amount</td>
                                        <td className='font-bold !text-[#101828]'>
                                            {decryptedValue[0]?.recipients ? CurrencyFormat(decryptedValue[0]?.recipients?.reduce((acc, sum) => acc + sum.amount, 0) || 0, true) : CurrencyFormat(decryptedValue?.reduce((acc, sum) => acc + sum.amount, 0) || 0, true)}
                                        </td>
                                    </tr>
                                </Table.Body>
                            </Table>

                            <div className='mt-7'>
                                <ContentLoading
                                    loading={false}
                                    data={
                                        <>
                                            {requestComments?.data?.length > 0 && <div className='mb-3 font-medium'>Comments</div>}
                                            {requestComments?.data?.length > 0 && requestComments?.data?.map((comment, index) => {
                                                const initials = nameInitials(comment?.businessMember?.firstName, comment?.businessMember?.lastName);
                                                return (
                                                    <div key={index} className='my-7'>
                                                        <div className='flex space-x-2'>
                                                            <div className='text-sm rounded-full w-8 h-8 flex justify-center items-center flex-shrink-0 ' style={{backgroundColor: commentColors[initials]}}>
                                                                {initials}
                                                            </div>
                                                            <div className='text-sm'>
                                                                <div>
                                                                    <span className='font-medium'>
                                                                        {comment?.businessMember?.firstName} {comment?.businessMember?.lastName}
                                                                    </span> {" "}
                                                                    <span className='text-[#718096]'>left a comment</span> • {moment(comment?.createdAt).fromNow()}
                                                                </div>
                                                                <div className='my-3'>{comment?.comment}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </>
                                    }
                                />
                            </div>

                            <div>
                                <div className='font-medium mb-2'>
                                    Leave a comment
                                </div>
                                <div className='relative w-full'>
                                    <textarea
                                        rows="5"
                                        disabled={requestCommentLoading}
                                        style={{resize: "none"}}
                                        placeholder='Enter your comment...'
                                        onChange={(e) => setForm({...form, comment: e.target.value})}
                                        value={form?.comment}
                                        autoFocus={false}
                                        className="form-input-textarea !rounded-lg placeholder-[#9ca3af]"
                                    />
                                    <div className='flex items-center space-x-2 absolute bottom-3 right-3'>
                                        <IconAttachment className='w-5 h-5'/>
                                        <button disabled={isFormValid} onClick={postRequestComment} className='btn bg-ep-primary-600 text-white'>Send</button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='w-2/4 px-5 overflow-y-auto'>
                            {/* Show ONLY approver approve/reject buttons when request is in pending */}
                            {userInfo?.member?.email !== request?.initiator?.email && (
                                request?.status?.toLowerCase() === "pending" ? (
                                    <>
                                        <div className='mb-1'>Actions:</div>
                                        <div className='flex items-center space-x-4'>
                                            <button className='custom-btn btn-lg font-medium btn-block focus:outline-none text-[#DC2626] border border-[#DC2626] bg-white'
                                                onClick={onOpenRejectRequest}
                                                >
                                                <span className='mr-2'><IconReject /></span>
                                                Reject
                                            </button>
                                            <button className='custom-btn btn-lg font-medium btn-block focus:outline-none text-[#009642] border border-[#009642] bg-white'
                                                onClick={onOpenApproveRequest}
                                                >
                                                <span className='mr-2'><IconApprove /></span>
                                                Approve
                                            </button>
                                        </div>
                                    </>
                                ) : null
                            )}

                            {/* Show ONLY initiator the cancel request */}
                            {userInfo?.member?.email === request?.initiator?.email && request?.status?.toLowerCase() === "pending" && (
                                <>
                                    <div className='mb-1'>Actions:</div>
                                    <div className='flex items-center space-x-4'>
                                        <div>
                                        <button className='custom-btn btn-lg font-medium btn-block focus:outline-none text-[#DC2626] border border-[#DC2626] bg-white'
                                            onClick={onOpenCancelRequest}
                                            >
                                            <span className='mr-2'><IconReject /></span>
                                            Cancel Request
                                        </button>
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className='my-7 text-sm'>
                                <Requester request={request} />
                                <div className='flex justify-between my-5'>
                                    <div className='text-[#667085]'>Request Type</div>
                                    <div className='font-medium capitalize'>{request?.type}</div>
                                </div>
                                <div className='flex justify-between my-5'>
                                    <div className='text-[#667085]'>Status</div>
                                    <div className='font-medium'>
                                        <div className={"label capitalize " + renderStatusColor(request?.status)}>
                                            {request?.status}
                                        </div>
                                    </div>
                                </div>
                                <div className='flex justify-between my-5'>
                                    <div className='text-[#667085]'>Date Requested</div>
                                    <div className='font-medium'>{DateFormat(request?.createdAt)} at {TimeFormat(request?.createdAt)}</div>
                                </div>
                            </div>
                            
                            <div>
                                <div className='font-medium mb-3'>Approval Workflow</div>
                                <StatusTimeline
                                    request={request}
                                />
                            </div>
                        </div>
                    </div>

                </div>

                {!approvalEmails?.includes(userInfo?.member?.email) && (
                    request?.status?.toLowerCase() === "approved" ? (
                        <div className='mt-7'>
                            <hr className='my-5'/>
                            <div className='flex justify-end'>
                                <div>
                                    <ButtonLoader loading={isLoading} className='btn btn-lg w-full rounded-lg whitespace-nowrap bg-ep-primary-600 border border-ep-primary-600 text-white'
                                        onClick={handleMakeTransfer}
                                        type="button"
                                    >
                                        {isLoading ? "Processing" : "Disburse"}
                                    </ButtonLoader>
                                </div>
                            </div>
                        </div>
                    ) : null
                )}
            </div>

        </FormDrawer>
    )
}

const Requester = memo(({ request }) => {
   return (
        <div className='flex justify-between my-5'>
            <div className='text-[#667085]'>Requester</div>
            <div className='flex items-center space-x-2'>
                <div className="text-xs rounded-full w-8 h-8 flex justify-center items-center flex-shrink-0 relative" style={{backgroundColor: getRandomColor()}}>
                    <div className="font-normal text-white">
                        {request?.initiator?.firstName?.charAt().toUpperCase()}{request?.initiator?.lastName.charAt().toUpperCase()}
                    </div>
                </div>
                <div className='font-medium capitalize'>{request?.initiator?.firstName} {request?.initiator?.lastName}</div>
            </div>
        </div>
    ) 
});

const StatusTimeline = memo(({request}) => {
	
	return (
        <>
            <div className={`w-full relative my-5 border-[#E5E7EB]`}>
                <div className='flex justify-between mb-2'>
                    <div className='flex space-x-2'>
                        <div className="text-xs">
                            <IconApproved className="text-xs" width={20} />
                        </div>

                        <div className="flex items-center">
                            <span className='text-[#718096] text-sm'>Request Sent</span>
                        </div>
                    </div>
                    <div className="text-sm text-[#718096]">
                        {DateFormat(request?.createdAt)} at {TimeFormat(request?.createdAt)}
                    </div>
                </div>
                
                <div className='absolute h-12 left-5 border-line-approval'></div>
                <div className='pl-7 flex items-center space-x-2'>
                    <div className="text-xs rounded-full w-8 h-8 flex justify-center items-center flex-shrink-0 relative" style={{backgroundColor: getRandomColor()}}>
                        <div className="font-normal text-white">
                            {request?.initiator?.firstName?.charAt().toUpperCase()}{request?.initiator?.lastName.charAt().toUpperCase()}
                        </div>
                    </div>
                    <div className='text-sm'>
                        <div>{request?.initiator?.firstName} {request?.initiator?.lastName}</div>
                        <div className='text-[#718096]'>{request?.initiator?.email}</div>
                    </div>
                </div>

                {request?.approvalWorkflow?.map(approver => (
                    <div key={approver?.id}>
                        <div className='flex justify-between mt-5'>
                            <div className='flex space-x-2'>
                                <div className="text-xs">
                                    {approver?.status === "approved" && <IconApproved className="text-xs" width={20} />}
                                    {approver?.status === "rejected" && <IconRejected className="text-xs" width={20} />}
                                    {approver?.status === "pending" && <IconPending className="text-xs" width={20} />}
                                </div>

                                <div className="flex items-center">
                                    <span className='text-[#718096] text-sm'>
                                        {approver?.status === "approved" && "Request Approved"}
                                        {approver?.status === "rejected" && "Request Rejected"}
                                        {approver?.status === "pending" && "Pending Approval"}
                                    </span>
                                </div>
                            </div>
                            <div className="text-sm text-[#718096]">
                                {approver?.reviewedAt && `${DateFormat(approver?.reviewedAt)} at ${TimeFormat(approver?.reviewedAt)}`}
                            </div>
                        </div>
                        <div className='pl-7 flex items-center space-x-2'>
                            <div className="text-xs rounded-full w-8 h-8 flex justify-center items-center flex-shrink-0 relative" style={{backgroundColor: getRandomColor()}}>
                                <div className="font-normal text-white">
                                    {approver?.businessMember?.firstName?.charAt().toUpperCase()}{approver?.businessMember?.lastName?.charAt().toUpperCase()}
                                </div>
                            </div>
                            <div className='text-sm'>
                                <div>{approver?.businessMember?.firstName} {approver?.businessMember?.lastName}</div>
                                <div className='text-[#718096]'>{approver?.businessMember?.email}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Maybe condition={request?.reason && request?.reason !== null}>
                <div className='mt-5'>
                    <div className='mb-4 font-medium'>Reason for Rejection</div>
                    <div className="w-full rounded-sm text-[#374151] text-justify text-sm mt-2">
                        {request?.reason}
                    </div>
                </div>
            </Maybe>
        </>
	)
});

export default RequestDetailsWrapper;